import type { MergeContext } from './mergeFields';

/**
 * Assembles the values that `applyMergeFields` substitutes.
 *
 * Kept separate from `mergeFields.ts` so that module stays free of domain imports
 * and runs unchanged on client and server. Substitution behaviour lives there;
 * *where the values come from* lives here.
 *
 * ## The booking link is fetched, not computed
 *
 * `<Payment Link>` and `<Travel File Link>` both resolve to the customer booking
 * portal URL — the same link the **Preview** button opens in Mail to Client
 * (`MailToClientModal`), and the same one the receipt email embeds.
 *
 * It is **not stored on the travel file**: `getBookingLink.ts` states it plainly —
 * *"a self-contained JWT (carries the TF id + expiry), so it is never persisted"*.
 * That is the better design, since the link is fully derivable from the TF id,
 * client and tour end date, and re-deriving it can never go stale.
 *
 * The consequence for this module: the URL is signed with a server secret, so a
 * browser cannot build it. The compose dialog fetches it from the existing
 * auth-gated `travelFilesApi.getBookingLink(id)` and passes it in as `bookingLink`.
 *
 * The two fields are one URL here because payment is an action *inside* the portal
 * page (`ViewBooking` inits Pelecard for the outstanding balance) rather than a
 * separate address. They are kept as two names so templates copied from Travel
 * Booster keep working.
 */

export interface MergeContextSources {
    /** The document being emailed, when there is one. Reports have no document. */
    document?: {
        type?: string | null;
        number?: number | string | null;
        info?: {
            travelFileNumber?: number | string | null;
            selectedPassengerFlatten?: { name?: string | null } | null;
            pdfInfo?: { passenger?: { name?: string | null } | null } | null;
        } | null;
    } | null;
    travelFile?: {
        /** The BOOKING number a customer recognises. `number` is agentClerk.number
         *  — a different thing, usually null — which is what this module used to
         *  read, so `<Booking Num>` came out empty on every compose. */
        refNumber?: number | string | null;
        number?: number | string | null;
        travelFileNumber?: number | string | null;
        startDate?: Date | string | null;
        endDate?: Date | string | null;
        balance?: number | null;
        lefttopay?: number | null;
        /** Populated object, or a bare FK id when it was never joined. */
        currency?: { code?: string | null; symbol?: string | null } | number | string | null;
        destination?: { name?: string | null } | string | number | null;
        destinationName?: string | null;
        /** Resolved by the caller — the travel file stores only the tour's id. */
        tourName?: string | null;
        /** Joined rows carry firstName/lastName; some callers pre-flatten to name. */
        passengers?: Array<{
            name?: string | null;
            fullName?: string | null;
            firstName?: string | null;
            lastName?: string | null;
            isLead?: boolean | null;
        }> | null;
    } | null;
    client?: { companyName?: string | null } | null;
    /** Booking portal URL, fetched via `travelFilesApi.getBookingLink(travelFileId)`. */
    bookingLink?: string | null;
    /** Explicit overrides win over anything derived — used by report screens. */
    overrides?: MergeContext;
}

/** dd/mm/yyyy — the format used across this system's documents. */
const formatDate = (value: Date | string | null | undefined): string => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
};

const firstNonEmpty = (...values: Array<string | number | null | undefined>): string => {
    for (const value of values) {
        if (value !== null && value !== undefined && String(value).trim() !== '') return String(value);
    }
    return '';
};

const formatAmount = (value: number | null | undefined): string =>
    value === null || value === undefined
        ? ''
        : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function buildMergeContext(sources: MergeContextSources = {}): MergeContext {
    const { document, travelFile, client, bookingLink, overrides = {} } = sources;

    // Lead first, then the first passenger. The joined shape carries
    // firstName/lastName rather than `name`, which is why reading only `name`
    // left every greeting as a bare "שלום רב, ".
    const passengers = travelFile?.passengers ?? [];
    const lead = passengers.find((p) => p?.isLead) ?? passengers[0];
    const passengerName = firstNonEmpty(
        document?.info?.selectedPassengerFlatten?.name,
        document?.info?.pdfInfo?.passenger?.name,
        lead?.name,
        lead?.fullName,
        [lead?.firstName, lead?.lastName].filter(Boolean).join(' ').trim()
    );

    // A destination is a populated object or a bare FK id. An id is NOT printed:
    // "תיק ההזמנה 5087" reads as a booking reference and is worse than a gap.
    const destinationName = firstNonEmpty(
        travelFile?.destinationName,
        typeof travelFile?.destination === 'object' ? travelFile?.destination?.name : undefined,
        typeof travelFile?.destination === 'string' ? travelFile?.destination : undefined
    );

    const currency = travelFile?.currency;
    const currencyLabel =
        currency && typeof currency === 'object'
            ? firstNonEmpty(currency.symbol, currency.code)
            : '';

    const context: MergeContext = {
        'First Customer Name': passengerName,
        'Booking Num': firstNonEmpty(
            document?.info?.travelFileNumber,
            travelFile?.refNumber,
            travelFile?.travelFileNumber,
            travelFile?.number
        ),
        'Company Name': firstNonEmpty(client?.companyName),
        Destination: destinationName,
        'Tour Name': firstNonEmpty(travelFile?.tourName),
        'Travel Start Date': formatDate(travelFile?.startDate),
        'Travel End Date': formatDate(travelFile?.endDate),
        // `type` is stored as its display string ('Tax Invoice'), already what a
        // recipient should read — no key-to-label lookup needed.
        'Document Type': firstNonEmpty(document?.type),
        'Document Number': firstNonEmpty(document?.number),
        'Booking Left To Pay': formatAmount(travelFile?.lefttopay ?? travelFile?.balance),
        'Booking Currency': currencyLabel,
        // One URL, two names — see the module comment.
        'Payment Link': bookingLink || '',
        'Travel File Link': bookingLink || ''
        // 'Missing Service Type' is engagement-only: it is derived from the rule's
        // cross-selling configuration, which has no meaning for a manual send.
    };

    return { ...context, ...overrides };
}

/**
 * Default subject when a template supplies none, matching the existing convention:
 * "Travel file # 2002148 for Mr. DROR/DAVID".
 */
export function buildDefaultSubject(context: MergeContext): string {
    const parts: string[] = [];
    if (context['Booking Num']) parts.push(`Travel file # ${context['Booking Num']}`);
    if (context['First Customer Name']) parts.push(`for ${context['First Customer Name']}`);
    if (!parts.length && context['Document Type']) {
        return context['Document Number']
            ? `${context['Document Type']} ${context['Document Number']}`
            : String(context['Document Type']);
    }
    return parts.join(' ');
}
